import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface InstallPromptState {
  /** The deferred beforeinstallprompt event (Chrome/Edge/Samsung) */
  deferredPrompt: BeforeInstallPromptEvent | null
  /** Whether the user has dismissed the banner */
  dismissed: boolean
  /** Timestamp of last dismissal — used to re-show after a cooldown */
  dismissedAt: number | null

  setDeferredPrompt: (event: BeforeInstallPromptEvent | null) => void
  dismiss: () => void
  resetDismissal: () => void
}

/** Re-show the banner after 7 days */
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

export const useInstallPromptStore = create<InstallPromptState>()(
  persist(
    (set) => ({
      deferredPrompt: null,
      dismissed: false,
      dismissedAt: null,

      setDeferredPrompt: (event) => set({ deferredPrompt: event }),
      dismiss: () => set({ dismissed: true, dismissedAt: Date.now(), deferredPrompt: null }),
      resetDismissal: () => set({ dismissed: false, dismissedAt: null }),
    }),
    {
      name: 'aime-install-prompt',
      partialize: (state) => ({
        dismissed: state.dismissed,
        dismissedAt: state.dismissedAt,
      }),
    },
  ),
)

/** Check if the dismissal cooldown has expired and reset if needed */
export function checkDismissalCooldown() {
  const { dismissed, dismissedAt, resetDismissal } = useInstallPromptStore.getState()
  if (dismissed && dismissedAt && Date.now() - dismissedAt > DISMISS_COOLDOWN_MS) {
    resetDismissal()
  }
}
